/**
 * The small set of glyphs the report needs, inlined.
 *
 * Inlining costs a few hundred bytes of bundle, removes a request per tile, and lets an icon take
 * its colour from the tile it sits in — which is the whole point of the green/amber/red
 * quick-check states.
 */

export type IconName =
  | 'shield'
  | 'calendar'
  | 'fuel'
  | 'bolt'
  | 'gauge'
  | 'warning'
  | 'globe'
  | 'check'
  | 'car'
  | 'leaf'
  | 'wrench'
  | 'euro'
  | 'search'
  | 'print'
  | 'info'
  | 'unlock'

interface IconProps {
  name: IconName
  size?: number
  className?: string
}

/**
 * Paths are drawn on a 24×24 grid with a 2px stroke, so every glyph carries the same optical
 * weight regardless of which one a tile picks.
 */
const PATHS: Record<IconName, string> = {
  shield: 'M12 3l7 3v6c0 4.5-3 7.7-7 9-4-1.3-7-4.5-7-9V6l7-3z',
  calendar: 'M4 6h16v14H4zM8 3v5M16 3v5M4 11h16',
  fuel: 'M4 20V5a1 1 0 011-1h7a1 1 0 011 1v15M3 20h11M4 11h9M16 8l3 3v6a2 2 0 01-4 0',
  bolt: 'M13 3L5 14h6l-1 7 8-11h-6l1-7z',
  gauge: 'M12 20a8 8 0 100-16 8 8 0 000 16zM12 12l4-3',
  warning: 'M12 4l9 16H3l9-16zM12 10v4M12 17.5v.5',
  globe: 'M12 21a9 9 0 100-18 9 9 0 000 18zM3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9S14.5 18.4 12 21c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z',
  check: 'M4 12.5l5.5 5.5L20 7',
  car: 'M5 16v2M19 16v2M3 16h18v-4l-2-5H5L3 12v4zM6.5 13h.01M17.5 13h.01',
  leaf: 'M5 19c0-8 5-13 15-13 0 9-5 13-11 13H5zM5 19c2-4 5-6 8-7',
  wrench: 'M15 4a5 5 0 00-4.6 7L4 17.4 6.6 20l6.4-6.4A5 5 0 0020 9l-3 3-2-2 3-3a5 5 0 00-3-3z',
  euro: 'M17 6a6 6 0 100 12M4 10h9M4 14h9',
  search: 'M11 18a7 7 0 100-14 7 7 0 000 14zM20 20l-4-4',
  print: 'M7 8V3h10v5M7 18H5a1 1 0 01-1-1v-6h16v6a1 1 0 01-1 1h-2M7 14h10v7H7z',
  info: 'M12 21a9 9 0 100-18 9 9 0 000 18zM12 11v6M12 7.5v.5',
  // An open padlock, its shackle lifted clear of the body on one side.
  unlock: 'M5 11h14v10H5zM8 11V7a4 4 0 017.9-1',
}

export function Icon({ name, size = 20, className }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  )
}
